// Motor LEVE de preview (TS puro, determinístico, efêmero). Modelo analítico fechado:
// disponibilidade A = MTBF/(MTBF+MTTR), agregação por estágios (série=min / paralelo=soma). NÃO
// modela filas/bloqueios/desempenho — isso é a rodada DETALHADA (DES real). Por isso o preview é
// rotulado "estimativa" e desempenho (P) fica em 100% (perdas de ritmo só na rodada detalhada).
import type { ConfidenceLevel } from "@opus-dt/shared-types";
import {
  applyPairProfile,
  deltaPctOf,
  effectiveFeedRate,
  MEASURABLE_PARAMS,
  originOf,
  previewConfidence,
  SIM_DEFAULTS,
} from "@opus-dt/shared-types";
import type { RunKpis, SandboxMachine, SandboxStage } from "@/types/sim-sandbox.types";
import { activeStages, machinesInStage } from "./SandboxCanvas/flow-helpers";

// Fallbacks conservadores p/ parâmetros ausentes (sempre contam como "premissa" na confiança).
// Conjunto ÚNICO compartilhado com API e worker (shared-types sim-defaults).
const DEFAULT_FEED_UN_H = SIM_DEFAULTS.feedRateUnH;
const DEFAULT_MTBF_MIN = SIM_DEFAULTS.mtbfMin;
const DEFAULT_MTTR_MIN = SIM_DEFAULTS.mttrMin;
const DEFAULT_QUALITY_PCT = SIM_DEFAULTS.qualityTargetPct;

export interface PreviewResult extends RunKpis {
  bottleneckStageKey: string | null;
  perStage: Array<{ stageKey: string; throughputUnH: number }>;
  confidenceLevel: ConfidenceLevel;
}

interface EffectiveParams {
  feedRateUnH: number;
  availability: number; // 0..1
  qualityPct: number;
  /** Nº de params medidos E não ajustados (0..4) — numerador da confiança.*/
  measuredParams: number;
}

export function availability(mtbfMin: number, mttrMin: number): number {
  const total = mtbfMin + mttrMin;
  return total > 0 ? mtbfMin / total : 0;
}

function deltaFor(machine: SandboxMachine, param: string): number {
  return 1 + deltaPctOf(machine.simOverrides, param) / 100;
}

function resolveParams(machine: SandboxMachine): EffectiveParams {
  // Base: perfil autorado → base MEDIDA do preview do servidor → default conservador.
  const p = machine.simProfile;
  const b = machine.measuredBase;
  // O perfil do PAR (máquina×produto) vem resolvido do servidor e é aplicado
  // aqui com o MESMO helper do worker e do preview server: a regra existe uma vez só.
  // Entra ANTES dos deltas (o slider ajusta ±25% sobre a base efetiva) e nunca vence um medido.
  const base = applyPairProfile(
    {
      feedRateUnH:
        p?.feedRateUnH ??
        (p?.cycleTimeS ? 3600 / p.cycleTimeS : undefined) ??
        b?.feedRateUnH ??
        DEFAULT_FEED_UN_H,
      mtbfMin: p?.mtbfMin ?? b?.mtbfMin ?? DEFAULT_MTBF_MIN,
      mttrMin: p?.mttrMin ?? b?.mttrMin ?? DEFAULT_MTTR_MIN,
      qualityTargetPct: p?.qualityTargetPct ?? b?.qualityTargetPct ?? DEFAULT_QUALITY_PCT,
      provenance: (p ?? b)?.provenance,
      sampleCount: (p ?? b)?.sampleCount,
      provenanceByParam: (p ?? b)?.provenanceByParam,
    },
    machine.pairProfile
  );
  const feedBase = base.feedRateUnH;
  const mtbf = base.mtbfMin * deltaFor(machine, "mtbf");
  const mttr = base.mttrMin * deltaFor(machine, "mttr");
  const quality = base.qualityTargetPct * deltaFor(machine, "quality");
  // Origem POR parâmetro: perfil autorado tem precedência; senão a base medida do
  // servidor. O par carimba "product" no mapa (não conta como medido). Ajuste manual
  // (delta≠0) rebaixa o param para premissa.
  const measuredParams = MEASURABLE_PARAMS.filter(
    (param) => originOf(base, param).provenance === "measured" && deltaFor(machine, param) === 1
  ).length;
  return {
    // Ritmo canônico: override de ciclo LEGADO dobra INVERSAMENTE — mesmo cálculo
    // do server/worker/DES, senão preview e rodada discordariam de gargalo.
    feedRateUnH: effectiveFeedRate(
      feedBase,
      deltaPctOf(machine.simOverrides, "feedRate"),
      deltaPctOf(machine.simOverrides, "cycleTime")
    ),
    availability: availability(mtbf, mttr),
    qualityPct: Math.min(quality, 100),
    measuredParams,
  };
}

const pct = (v: number): number => Math.round(v * 1000) / 10; // 1 casa

/** Roda o motor leve sobre o rascunho. Determinístico: mesma entrada ⇒ mesma saída.*/
export function computePreview(input: {
  machines: SandboxMachine[];
  stages: SandboxStage[];
  horizonHours: number;
  /** Horas OPERACIONAIS RECEBIDAS do preview server (∩ calendário). O cliente NUNCA
   * resolve a regra do calendário: quando presente, a produção usa estas horas no lugar das
   * corridas. Ausente ⇒ 24/7 (usa horizonHours). Muda só com período/calendário.*/
  effectiveHours?: number;
  /** Janela de calibração p/ o fator de confiança (espelha o server). Default 90.*/
  calibrationDays?: number;
}): PreviewResult {
  const ordered = activeStages(input.stages, input.machines);
  if (ordered.length === 0) {
    return {
      oee: null,
      availability: null,
      performance: null,
      quality: null,
      estimatedProduction: null,
      confidence: "INSUFFICIENT",
      confidenceLevel: "INSUFFICIENT",
      bottleneckNames: [],
      bottleneckStageKey: null,
      perStage: [],
    };
  }

  const perStage: Array<{ stageKey: string; throughputUnH: number; nominal: number }> = [];
  let measuredCount = 0;
  let totalCount = 0;
  let qualityLine = 1;

  for (const stage of ordered) {
    const machines = machinesInStage(stage.key, input.machines);
    let throughput = 0; // Σ feed×A (paralelo = soma dentro do estágio)
    let nominal = 0; // Σ feed (A=1) p/ derivar disponibilidade de linha
    let stageQuality = 1; // pior máquina gatilha (min) — defeitos compõem entre estágios
    for (const m of machines) {
      const e = resolveParams(m);
      throughput += e.feedRateUnH * e.availability;
      nominal += e.feedRateUnH;
      stageQuality = Math.min(stageQuality, e.qualityPct / 100);
      // Denominador = 4 params deriváveis por máquina (espelha o server).
      totalCount += MEASURABLE_PARAMS.length;
      measuredCount += e.measuredParams;
    }
    qualityLine *= stageQuality;
    perStage.push({ stageKey: stage.key, throughputUnH: throughput, nominal });
  }

  // Linha = min entre estágios (série); gargalo = estágio de menor vazão.
  const bottleneck = perStage.reduce((a, b) => (b.throughputUnH < a.throughputUnH ? b : a));
  const lineThroughput = bottleneck.throughputUnH;
  const lineNominal = perStage.find((s) => s.stageKey === bottleneck.stageKey)?.nominal ?? 0;
  const availPct = lineNominal > 0 ? (lineThroughput / lineNominal) * 100 : 0;
  const performancePct = 100; // preview não modela perda de ritmo (só a rodada detalhada)
  const qualityPct = qualityLine * 100;
  const oee = (availPct / 100) * (performancePct / 100) * qualityPct; // = A×P×Q em %
  // Produção sobre horas OPERACIONAIS quando o server as informou (calendário); senão
  // corridas (24/7). O cliente só CONSOME o escalar — nunca resolve o calendário.
  const hoursForProduction = input.effectiveHours ?? input.horizonHours;
  const production = lineThroughput * hoursForProduction * (qualityPct / 100);

  const level = previewConfidence(measuredCount, totalCount, input.calibrationDays ?? 90);
  const bottleneckMachines = machinesInStage(bottleneck.stageKey, input.machines).map(
    (m) => m.name
  );

  return {
    oee: pct(oee / 100),
    availability: pct(availPct / 100),
    performance: performancePct,
    quality: pct(qualityPct / 100),
    estimatedProduction: Math.round(production),
    confidence: level,
    confidenceLevel: level,
    bottleneckNames: bottleneckMachines,
    bottleneckStageKey: bottleneck.stageKey,
    perStage: perStage.map((s) => ({
      stageKey: s.stageKey,
      throughputUnH: Math.round(s.throughputUnH),
    })),
  };
}
